import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { chooseStepLevel, TRAIL_RESERVE_PX, type StepLevel } from './stepWindow';

function px(value: string | null | undefined): number {
  const n = parseFloat(value ?? '');
  return Number.isFinite(n) ? n : 0;
}

/**
 * Width the tracker may use: the bar's inner width, minus everything else on
 * the row (the sibling buttons at every level between the tracker and the bar)
 * and the room kept for the last crumb of the trail.
 */
function availableWidth(host: HTMLElement, bar: HTMLElement): number {
  const barStyle = window.getComputedStyle(bar);
  let free = bar.clientWidth - px(barStyle.paddingLeft) - px(barStyle.paddingRight) - TRAIL_RESERVE_PX;
  let node: HTMLElement = host;
  while (node !== bar && node.parentElement) {
    const parent: HTMLElement = node.parentElement;
    const gap = px(window.getComputedStyle(parent).columnGap);
    const siblings = Array.from(parent.children).filter(
      (c): c is HTMLElement => c !== node && c instanceof HTMLElement && !c.classList.contains('global-breadcrumb-left'),
    );
    free -= siblings.reduce((total, c) => total + c.offsetWidth, 0) + gap * siblings.length;
    node = parent;
  }
  return free;
}

/**
 * Decides how many steps of a tracker fit. The natural width of each step is
 * measured while all of them are on screen (a layout effect, so the first
 * paint is already right), then a ResizeObserver on the bar re-decides as the
 * viewport changes. `stepsKey` changes when the steps themselves do (a label
 * changing from "Decision" to "Rejected"), which re-measures.
 */
export function useStepCollapse(stepsKey: string, currentIndex: number) {
  const hostRef = useRef<HTMLDivElement>(null);
  const widthsRef = useRef<number[]>([]);
  const [level, setLevel] = useState<StepLevel>('all');
  const [measuredKey, setMeasuredKey] = useState<string | null>(null);

  const decide = useCallback(() => {
    const host = hostRef.current;
    const bar = host?.closest<HTMLElement>('.global-breadcrumb-bar') ?? null;
    if (!host || !bar || widthsRef.current.length === 0) return;
    setLevel(chooseStepLevel(widthsRef.current, currentIndex, availableWidth(host, bar)));
  }, [currentIndex]);

  // Measure from a full render.
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (measuredKey !== stepsKey) {
      setLevel('all');
      setMeasuredKey(stepsKey);
      return;
    }
    if (level === 'all') {
      const chips = Array.from(host.querySelectorAll<HTMLElement>('.header-status-step'));
      widthsRef.current = chips.map(c => c.offsetWidth);
    }
    decide();
  }, [stepsKey, measuredKey, level, decide]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const bar = host?.closest<HTMLElement>('.global-breadcrumb-bar');
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => decide());
    observer.observe(bar);
    return () => observer.disconnect();
  }, [decide]);

  return { hostRef, level };
}
